import api from "@/utils/api";
import { buildMirrorEvent } from "@/utils/shareTracker";

/**
 * 전량 행동데이터 배치 큐 (P0-2/NZSC-3208).
 *
 * track_* / mixpanel_track 미러 이벤트를 브라우저 메모리에 모았다가 묶어서 1요청으로 전송한다.
 * 이벤트당 요청을 안 보내므로 api-public throttle/부하에 영향이 없다(기존 서비스 보호).
 *
 * flush 트리거:
 *   ① 20개 쌓이면  ② 5초마다  ③ 페이지 떠날 때(visibilitychange hidden / pagehide → sendBeacon)
 * 유실 방지: 페이지 이탈 sendBeacon(탭 닫혀도 전송) + 실패 시 localStorage 재시도 큐 + event_id 멱등.
 * 전송: { _track_only:true, events:[...] } → 컨트롤러가 bridge 스킵하고 각 이벤트 SQS 발사.
 */

const FLUSH_SIZE = 20;
const FLUSH_MS = 5000;
const MAX_BATCH = 50;
const RETRY_KEY = "nz_behavior_retry";
const RETRY_CAP = 300;
const ENDPOINT = "/api/v1/user-events";

let queue = [];
let timer = null;

/** 미러 이벤트 1건 적재 — 네트워크 없이 메모리 push 만(거의 공짜) */
export function enqueueBehavior(eventName, properties = {}) {
  try {
    queue.push(buildMirrorEvent(eventName, properties));
    if (queue.length >= FLUSH_SIZE) flush();
    else scheduleFlush();
  } catch {
    /* 절대 throw 안 함 — 미러가 서비스/트래킹에 영향 0 */
  }
}

function scheduleFlush() {
  if (timer) return;
  timer = setTimeout(() => flush(), FLUSH_MS);
}

/** 큐를 묶어 전송. useBeacon=true 면 sendBeacon(페이지 이탈용). */
function flush(useBeacon = false, keepalive = false) {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0) return;
  const events = queue.splice(0, MAX_BATCH);
  send({ _track_only: true, events }, useBeacon, keepalive);
  if (queue.length > 0) scheduleFlush(); // MAX_BATCH 넘게 쌓였으면 다음 회차
}

/** 풀 페이지 이동 직전 강제 전송 — NZSC-3797.
 *  라우터 가드가 컷오버 라우트로 window.location.href 를 걸면 pagehide 가 오기 전에
 *  페이지가 파기돼 직전 click_action/exit_screen 이 통째로 사라진다.
 *  실측: /mypage 하단 "분석" 탭 tab_analyze 가 8/21 까지 일 43~105건 → 8/22 부터 0~4건
 *  (/main·/subscribe·/doby/landing 동반). web-core 는 같은 문제를 NZSC-3642 에서
 *  이미 keepalive fetch 로 고쳤고 Vue 쪽만 남아 있었다. */
export function flushBehaviorNow() {
  try {
    flush(false, true);
  } catch {
    /* 계측 실패가 이동을 막아선 안 된다 */
  }
}

function absoluteUrl() {
  const base = (api && api.defaults && api.defaults.baseURL) || "";
  return base ? base.replace(/\/$/, "") + ENDPOINT : ENDPOINT;
}

function send(payload, useBeacon, keepalive = false) {
  const body = JSON.stringify(payload);
  try {
    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      // text/plain — preflight 없이 페이지 닫혀도 전송. 실패 시 백업.
      const ok = navigator.sendBeacon(absoluteUrl(), new Blob([body], { type: "text/plain" }));
      if (!ok) backup(payload.events);
      return;
    }
    if (keepalive && typeof fetch === "function") {
      // 이동 직전 경로 — 페이지가 파기돼도 완주한다. sendBeacon 을 쓰지 않는 이유는
      // web-core 실측(NZSC-3642)과 같다: 요청은 나가는데 적재가 안 됐다.
      const accessToken = localStorage.getItem("naezip_to_ac_se");
      fetch(absoluteUrl(), {
        method: "POST",
        credentials: "include",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body,
      })
        .then(res => {
          if (!res.ok) backup(payload.events);
        })
        .catch(() => backup(payload.events));
      return;
    }
    api.post(ENDPOINT, payload).catch(() => backup(payload.events));
  } catch {
    backup(payload.events);
  }
}

/** 전송 실패분을 localStorage 에 백업 → 다음 진입 때 재시도(멱등 event_id 라 중복 안전) */
function backup(events) {
  try {
    const prev = JSON.parse(localStorage.getItem(RETRY_KEY) || "[]");
    const merged = prev.concat(events).slice(-RETRY_CAP);
    localStorage.setItem(RETRY_KEY, JSON.stringify(merged));
  } catch {
    /* localStorage 불가 — 무시 */
  }
}

/** 앱 시작 시 1회 — 재시도 큐 드레인 + 페이지 이탈 flush 리스너 등록 */
export function initBehaviorQueue() {
  if (typeof window === "undefined") return;
  try {
    const retry = JSON.parse(localStorage.getItem(RETRY_KEY) || "[]");
    if (Array.isArray(retry) && retry.length) {
      localStorage.removeItem(RETRY_KEY);
      queue.push(...retry);
      scheduleFlush();
    }
  } catch {
    /* 무시 */
  }
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
  window.addEventListener("pagehide", () => flush(true));
}
